import type { ModuleAgentContribution } from "../../runtime/contracts";
import { Icon } from "../../components/ui";
import { defineModule } from "../_base/defineModule";
import { EpisodeCard, episodeHasMore } from "./EntityCards";
import { EpisodeChatItemContent } from "./EpisodeChatItemContent";
import { EpisodeDetailWrapper } from "./EpisodeDetailWrapper";
import { EpisodeContextPane } from "./EpisodeContextPane";
import { AgentModule as AgentModuleComponent } from "./AgentModule";
import { createAgentModuleStore } from "./store";
import { getSourceVisual } from "./helpers";
import { formatTimeAgo } from "../../utils/time";
import { pickAvatarColor } from "../../utils/avatar";
import type { ListItem } from "../_base/types";
import type { EpisodeListItem } from "./types";

export { EpisodeChatItemContent } from "./EpisodeChatItemContent";
export { EpisodeDetailWrapper } from "./EpisodeDetailWrapper";
export { EpisodeContextPane } from "./EpisodeContextPane";
// eslint-disable-next-line react-refresh/only-export-components
export { mapEpisodeToListItem };

function mapEpisodeToListItem(raw: Record<string, unknown>): ListItem {
  const ep = raw as unknown as EpisodeListItem;
  // Plan #8: no context_key — list items use generic episode visuals.
  // Precise source-module visuals come from the started_with link on
  // EpisodeDetail / EpisodeContextPane surfaces.
  const sourceModuleId = "episodes";
  const visuals = getSourceVisual(sourceModuleId);
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const activityAt = ep.date ?? ep.last_message_at ?? ep.updated_at ?? ep.created_at;

  return {
    id: ep.id,
    name: ep.title,
    schema_id: "episodes.episode",
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    preview: `${ep.message_count} messages`,
    timestamp: activityAt,
    is_archived: ep.status === "archived",
    metadata: {
      iconName: visuals.icon,
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      color: pickAvatarColor(ep.title) ?? visuals.color,
      time: formatTimeAgo(activityAt),
      status: ep.status,
      sourceModuleId,
    },
  };
}

const agentContribution: Partial<Parameters<typeof defineModule>[0]> & {
  navigateToEntity: ModuleAgentContribution["navigateToEntity"];
} = {
  // eslint-disable-next-line @typescript-eslint/require-await
  navigateToEntity: async (entityId, _schemaId, _data, _runtime, navigate) => {
    navigate("episodes", "episode", entityId);
  },
};

const _episodesModuleDef = defineModule({
  id: "episodes",
  title: "Chats",
  icon: <Icon name="message-circle" size={26} />,
  iconName: "message-circle",
  themeColor: "orange",
  entityTypes: ["episode"],
  primaryEntityType: "episode",
  rpc: { list: "episodes.list", get: "episodes.get", update: "episodes.set_title" },
  enableListRename: true,
  mapRenameParams: (id, name) => ({ episode_id: id, title: name }),
  ListItemContent: EpisodeChatItemContent,
  detailType: "custom",
  DetailPanel: EpisodeDetailWrapper,
  RightPaneComponent: EpisodeContextPane,
  groupBy: "date",
  getGroupDate: (item) => (item.timestamp ? new Date(item.timestamp) : null),
  mapListItem: mapEpisodeToListItem,
  contextMenuItems: (item) => {
    const isArchived = item.is_archived === true;
    return [
      {
        id: isArchived ? "unarchive" : "archive",
        label: isArchived ? "Unarchive" : "Archive",
        icon: isArchived ? "archive-restore" : "archive",
      },
    ];
  },
  navigateToEntity: agentContribution.navigateToEntity,
  entityLabels: {
    episode: {
      icon: "message-circle",
      label: "Episode",
      EntityCard: EpisodeCard,
      hasMore: episodeHasMore,
    },
  },
});

// Override Component with the custom AgentModule that handles
// pendingAgentQuery, URL sync, and episode creation.
// eslint-disable-next-line react-refresh/only-export-components
export const AgentModule = {
  ..._episodesModuleDef,
  Component: AgentModuleComponent,
  createStore: createAgentModuleStore,
};
